'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const primaryNavItems = [
    { label: 'Home', href: '/', icon: 'HomeIcon' },
    { label: 'Conservation Data', href: '/conservation-data-center', icon: 'ChartPieIcon' },
    { label: 'News & Updates', href: '/news-updates', icon: 'NewspaperIcon' },
    { label: 'Podcasts', href: '/podcasts', icon: 'MicrophoneIcon' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-card shadow-organic ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 lg:px-8">
          {/* Logo Section - No left padding */}
          <Link href="/impact-dashboard-homepage" className="flex items-center space-x-3 flex-shrink-0">
            <div className="relative w-10 h-10">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                <path
                  d="M20 5C11.716 5 5 11.716 5 20C5 28.284 11.716 35 20 35C28.284 35 35 28.284 35 20C35 11.716 28.284 5 20 5Z"
                  fill="var(--color-primary)"
                />
                <path
                  d="M15 18C15 16.343 16.343 15 18 15C19.657 15 21 16.343 21 18C21 19.657 19.657 21 18 21C16.343 21 15 19.657 15 18Z"
                  fill="var(--color-background)"
                />
                <path
                  d="M25 15C23.343 15 22 16.343 22 18C22 19.657 23.343 21 25 21C26.657 21 28 19.657 28 18C28 16.343 26.657 15 25 15Z"
                  fill="var(--color-background)"
                />
                <path
                  d="M12 25C12 23.5 14 22 20 22C26 22 28 23.5 28 25V27H12V25Z"
                  fill="var(--color-background)"
                />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-headline font-bold text-primary leading-tight">Rhinos&amp;Rangers</span>
              <span className="text-xs text-muted-foreground font-body">Conservation Intelligence</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-body font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-organic duration-300"
              >
                <Icon name={item.icon as any} size={18} variant="outline" />
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/donations"
              className="px-6 py-2.5 bg-warning text-warning-foreground font-cta text-sm font-semibold rounded-md hover:bg-warning/90 shadow-organic-sm transition-organic duration-300"
            >
              Donate Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-organic duration-300"
            aria-label="Toggle menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border">
            <nav className="px-4 py-4 space-y-1">
              {primaryNavItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 px-4 py-3 text-sm font-body font-medium text-foreground hover:bg-muted hover:text-primary rounded-md transition-organic duration-300"
                >
                  <Icon name={item.icon as any} size={20} variant="outline" />
                  <span>{item.label}</span>
                </Link>
              ))}

              <div className="pt-4">
                <Link
                  href="/donations"
                  onClick={closeMobileMenu}
                  className="block w-full px-6 py-3 bg-warning text-warning-foreground text-center font-cta text-sm font-semibold rounded-md hover:bg-warning/90 shadow-organic-sm transition-organic duration-300"
                >
                  Donate Now
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;